import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../lib/auth";
import { api, apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Crown, Check, Loader2, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const PLANS = [
  { id: "monthly", name: "Monthly", price: "₹299", period: "/month", tag: "Flexible" },
  { id: "yearly",  name: "Yearly",  price: "₹2,499", period: "/year",  tag: "Save 30%" },
];
const PERKS = [
  "Unlimited AI notes for every chapter",
  "Voice chat + photo-solve tutor",
  "Fresh quizzes & mock tests",
  "Sample board papers with model answers",
  "Priority updates",
];

export default function Subscribe() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [links, setLinks] = useState({ monthly: "", yearly: "" });
  const [pending, setPending] = useState(null); // {plan}
  const [paymentId, setPaymentId] = useState("");
  const [activating, setActivating] = useState(false);

  // Pre-fetch payment link URLs so anchors have href BEFORE user taps —
  // avoids mobile popup-blocker (which triggers when window.open is called
  // after an await).
  useEffect(() => {
    api.get("/subscription/links")
      .then((r) => setLinks({ monthly: r.data.monthly.link, yearly: r.data.yearly.link }))
      .catch(() => {});
  }, []);

  const onLinkClick = (plan) => {
    // Fire-and-forget order record (does not block the anchor navigation)
    api.post(`/subscription/create-order?plan=${plan}`).catch(() => {});
    // Show the "I've paid" dialog after a short delay so it's ready when the
    // user comes back from the Razorpay tab.
    setTimeout(() => setPending({ plan }), 400);
  };

  const confirmPayment = async () => {
    if (!pending) return;
    setActivating(true);
    try {
      // Create the order (or reuse), then confirm
      const { data: order } = await api.post(`/subscription/create-order?plan=${pending.plan}`);
      await api.post("/subscription/confirm-payment", null, {
        params: { order_id: order.order_id, razorpay_payment_id: paymentId || "" },
      });
      await refreshUser();
      toast.success(`${pending.plan === "monthly" ? "Monthly" : "Yearly"} plan activated!`);
      setPending(null); setPaymentId("");
      nav("/dashboard");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setActivating(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-block brut-border bg-[#FFF5D1] px-3 py-1 brut-shadow-sm mb-3">
            <Crown size={14} className="inline mr-1" strokeWidth={2.5}/>
            <span className="text-xs font-bold tracking-[0.2em] uppercase">Upgrade</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight">Keep learning without limits</h1>
          <p className="text-neutral-700 mt-3 max-w-xl mx-auto text-sm sm:text-base px-2">
            Your 20-day free trial gives you the full experience. Continue with a plan that suits you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          {PLANS.map((p) => {
            const href = links[p.id] || "#";
            const disabled = !links[p.id];
            return (
              <div key={p.id} className={`brut-card p-5 sm:p-6 overflow-hidden ${p.id === "yearly" ? "bg-[#E9E6FA]" : "bg-white"}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-widest">{p.tag}</div>
                  <Crown size={18} strokeWidth={2.5}/>
                </div>
                <div className="mt-3 sm:mt-4 text-xl sm:text-2xl font-bold">{p.name}</div>
                <div className="mt-1 flex items-baseline gap-1 flex-wrap">
                  <span className="text-3xl sm:text-5xl font-black leading-none">{p.price}</span>
                  <span className="text-neutral-600 text-sm sm:text-base">{p.period}</span>
                </div>
                <ul className="mt-4 sm:mt-5 space-y-2">
                  {PERKS.map((per, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={16} strokeWidth={2.5} className="mt-0.5 flex-none"/>
                      <span>{per}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { if (disabled) e.preventDefault(); else onLinkClick(p.id); }}
                  aria-disabled={disabled}
                  data-testid={`subscribe-${p.id}`}
                  className={`brut-btn rounded-none w-full mt-5 sm:mt-6 py-4 sm:py-5 text-sm sm:text-base
                    inline-flex items-center justify-center gap-2 font-bold
                    ${disabled ? "bg-neutral-300 text-neutral-500 pointer-events-none"
                                : "bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]"}`}>
                  {disabled ? <><Loader2 size={16} className="animate-spin"/> Loading…</>
                            : <>Pay {p.price} <ExternalLink size={14} strokeWidth={2.5}/></>}
                </a>
              </div>
            );
          })}
        </div>

        <div className="brut-card p-4 mt-6 sm:mt-8 bg-[#FFF5D1] text-xs sm:text-sm">
          <b>How this works:</b> tapping Pay opens a secure Razorpay page in a new tab.
          Complete the payment there, then come back here and tap
          <b> "I've paid — activate"</b>. Your subscription unlocks instantly.
          <br/>Current plan: <b>{user?.subscription_plan || "-"}</b>.
        </div>
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent className="brut-border brut-shadow bg-white rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-black text-2xl">Complete your payment</DialogTitle>
            <DialogDescription>
              Finish paying on the Razorpay tab that just opened. Then return here to activate your subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pending && links[pending.plan] && (
              <a href={links[pending.plan]} target="_blank" rel="noopener noreferrer"
                 className="brut-border brut-shadow-sm bg-[#FFF5D1] p-3 flex items-center justify-between font-bold hover:bg-[#FFE066]"
                 data-testid="reopen-payment-link">
                Reopen Razorpay page <ExternalLink size={14} strokeWidth={2.5}/>
              </a>
            )}
            <div className="text-xs sm:text-sm">
              Optional: paste the <b>Payment ID</b> (starts with <code>pay_</code>) from your Razorpay
              confirmation for our records.
            </div>
            <Input
              data-testid="payment-id-input"
              value={paymentId}
              onChange={(e) => setPaymentId(e.target.value)}
              placeholder="pay_XXXXXXXXXX (optional)"
              className="brut-input rounded-none"
            />
            <Button
              data-testid="confirm-payment-btn"
              onClick={confirmPayment}
              disabled={activating}
              className="brut-btn rounded-none w-full bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] py-5">
              {activating
                ? <><Loader2 size={16} className="mr-2 animate-spin"/>Activating…</>
                : "I've paid — activate my subscription"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
